// Copyright (c) Ensho Health, 2021 to present

import React, { useEffect, lazy, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, useHistory, useLocation } from 'react-router-dom';
import { Security, SecureRoute, LoginCallback } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { ROUTES, LOCAL_STORAGE } from '../constants.js';
import { oktaClient } from '../okta.js';
import { useOktaAuth } from '@okta/okta-react';
import actions from '../state-manager/action/index.js';

import Notification from '../component/notification/Notification.js';

// App pages
import Login from './login/Login.js';
import Welcome from './welcome/Welcome.js';
import ForgotPassword from './forgot-password/ForgotPassword.js';
import ResetPassword from './reset-password/ResetPassword.js';
import BlurLoader from '../component/blur-loader/BlurLoader.js';
// components for secured routes
const UserSetup = lazy(() =>
  import(
    /* webpackChunkName: "protected-user-setup"*/ './user-setup/UserSetup.js'
  )
);
const ResultList = lazy(() =>
  import(
    /* webpackChunkName: "protected-result-list"*/ './result-list/ResultList.js'
  )
);
const PatientResult = lazy(() =>
  import(
    /* webpackChunkName: "protected-patient-result"*/ './patient-result/PatientResult.js'
  )
);
const Algorithms = lazy(() =>
  import(
    /* webpackChunkName: "protected-patient-result"*/ './algorithms/Algorithms.js'
  )
);
const Requisition = lazy(() =>
  import(
    /* webpackChunkName: "protected-requisition"*/ './requisition/RequisitionOrder.js'
  )
);
const AlgorithmInfo = lazy(() =>
  import(
    /* webpackChunkName: "protected-info"*/ './algorithms/AlgorithmInfo.js'
  )
);

function App() {
  const history = useHistory();

  const onAuthRequired = () => {
    history.push('/login');
  };
  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    history.replace(toRelativeUrl(originalUri, window.location.origin));
  };

  return (
    <Security
      oktaAuth={oktaClient}
      restoreOriginalUri={restoreOriginalUri}
      onAuthRequired={onAuthRequired}
    >
      <Notification />
      <Router />
    </Security>
  );
}

const Router = (props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { authState } = useOktaAuth();
  const user = useSelector((state) => state.user);
  const app = useSelector((state) => state.app);

  // Reacting on the user being logged in
  useEffect(() => {
    async function initApp() {
      try {
        await dispatch(actions.app.init());
      } catch (error) {
        console.error(error.message);
      }
    }
    if (authState?.isAuthenticated) {
      initApp();
    }
  }, [dispatch, authState?.isAuthenticated]);

  // Reacting on the user data being populated
  // this use effect must listen to all the data that need to be initialized
  // via different screens.
  // For example, the user setup data (last name, ...), the clinic unmasking key
  // etc...
  useEffect(() => {
    if (user?.data.userId && !app.loading) {
      let redirect = null;
      if (!user.data.setupComplete) {
        // user setup
        redirect = ROUTES.USER_SETUP;
      } else {
        // the user attempted to reach a url without being logged in.
        redirect = localStorage.getItem(LOCAL_STORAGE.LOGIN_REDIRECT);
        if (redirect) {
          localStorage.removeItem(LOCAL_STORAGE.LOGIN_REDIRECT);
        }
      }

      // if any of the tests above returned true, redirect is now a path
      // to where the user must be redirected
      const fullUrl = window.location.href;
      // Saving both the route and the URL parameters. I don't think there is
      // a more straightforward way to do this using react-router-dom.
      const currentRoute = fullUrl.split(window.location.host)[1];
      if (redirect && redirect !== currentRoute) {
        // saving the current user's destination so we can redirect them to it
        // afterward. We have to be careful to not store the setup urls
        const setupUrls = [ROUTES.USER_SETUP, ROUTES.LOGIN, ROUTES.HOME];
        if (!setupUrls.includes(currentRoute)) {
          localStorage.setItem(LOCAL_STORAGE.LOGIN_REDIRECT, currentRoute);
        }
        history.push(redirect);
      }
    }
  }, [
    history,
    user?.data.userId,
    location.pathname,
    user?.data?.setupComplete,
    app.loading
  ]);
  if (!app.loading || user.data.userId !== undefined) {
    return (
      <Suspense fallback={<BlurLoader loading={true} />}>
        <SecureRoute exact path={ROUTES.HOME} component={ResultList} />
        <Route path={ROUTES.LOGIN} component={Login} />
        <Route path={ROUTES.FORGOT_PASSWORD} component={ForgotPassword} />
        <Route path={ROUTES.RESET_PASSWORD} component={ResetPassword} />
        <Route path={ROUTES.WELCOME} component={Welcome} />
        <Route path={ROUTES.LOGIN_CALLBACK} component={LoginCallback} />
        <SecureRoute path={ROUTES.USER_SETUP} component={UserSetup} />
        <SecureRoute path={ROUTES.RESULT_LIST} component={ResultList} />
        <SecureRoute path={ROUTES.PATIENT_RESULT} component={PatientResult} />
        <SecureRoute exact path={ROUTES.ALGORITHMS} component={Algorithms} />
        <SecureRoute path={ROUTES.ALGORITHMS_INFO} component={AlgorithmInfo} />
        <SecureRoute path={ROUTES.REQUISITION} component={Requisition} />
      </Suspense>
    );
  } else {
    return <BlurLoader loading={true} />;
  }
};

export default App;
